import React, { useState, useEffect } from "react";
import { Lock, Users, Globe, UserCheck, HeartHandshake, Eye } from "lucide-react";
import { ReactComponent as HatGlasses } from 'assets/icon-hatglasses.svg'
import { 
    DropdownMenu, 
    DropdownMenuContent, 
    DropdownMenuItem, 
    DropdownMenuTrigger 
} from "ui/DropdownMenu";
import { Link } from "react-router-dom";
import { Popover, PopoverTrigger, PopoverContent } from "ui/Popover";
import { PrivacyLevel } from "util/userUtils";
import api from "config/axiosConfig";
import { toast } from "sonner";
import { ViewerRelation } from "util/constants";
import { apiRoutes } from "util/apiRoutes";

export interface PrivacyOption {
    value: string;
    label: string;
    icon: React.ElementType;
}

export interface ViewerOption {
    value: string;
    label: string;
}

interface EntityPrivacyProps {
    /**
     * The current privacy level of the entity
     */
    privacy: PrivacyLevel;
    
    /**
     * Callback function when privacy changes
     */
    onPrivacyChange: (privacy: PrivacyLevel) => void;
    
    /**
     * Whether the component is for a user or team entity
     */
    actorType?: "user" | "team";
    
    /**
     * Whether the entity is in edit mode (existing entity)
     */
    editMode?: boolean;
    
    /**
     * The entity ID (only needed for editMode)
     */
    entityId?: number;
    
    /**
     * The entity type (e.g., 'impact', 'solution', 'comment', 'feedback')
     */
    entityType?: string;

    /**
     * Optional CSS class name for styling
     */
    className?: string;

    /**
     * Whether to use a mini icon
     */
    miniIcon?: boolean;

    /**
     * Whether to show dropdown menu
     */
    showDropdown?: boolean;

    /**
     * Show viewer relation
     */
    viewerRelation?: ViewerRelation;

    /**
     * Whether to hide the anonymous option
     */
    hideAnonymous?: boolean;

    /**
     * Whether to show only public option
     */
    publicOnly?: boolean;
}

/**
 * A reusable component for managing entity privacy settings
 * Works for both new entities (during creation) and existing entities (edit mode)
 */
const EntityPrivacy: React.FC<EntityPrivacyProps> = ({
    privacy,
    onPrivacyChange,
    actorType = "user",
    editMode = false,
    entityId,
    entityType,
    className,
    miniIcon = false,
    showDropdown = true,
    viewerRelation = 'self',
    hideAnonymous = false,
    publicOnly = false,
}) => {
    const [currentPrivacy, setCurrentPrivacy] = useState<PrivacyLevel>(privacy);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [currentViewerRelation, setCurrentViewerRelation] = useState<ViewerRelation>(viewerRelation);
    // Update local state when props change
    useEffect(() => {
        setCurrentPrivacy(privacy);
        setCurrentViewerRelation(viewerRelation);
    }, [privacy, viewerRelation]);

    // When `publicOnly` is active (e.g. the rebut composer for a
    // teabate argument) the dropdown is restricted to
    // [anonymous, public]. If the caller's stored privacy is some
    // other audience — `me`, `besteas`, `follow`, `teams` —
    // surface that mismatch back to the parent so its submit
    // payload doesn't ship a value the dropdown can no longer
    // represent. Without this coercion, rebut submits would keep
    // the user's default-audience leak through to the backend.
    useEffect(() => {
        if (!publicOnly) return;
        if (currentPrivacy === 'public' || currentPrivacy === 'anonymous') return;
        setCurrentPrivacy('public');
        onPrivacyChange('public');
        // `onPrivacyChange` is treated as a stable callback by every
        // caller in CRA today; depending on it would just churn the
        // effect without changing behavior.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [publicOnly, currentPrivacy]);

    const getViewerMessage = () => {
        if (currentPrivacy === 'anonymous') {
            return `This ${entityType} is posted anonymously and visible to everyone, but the author's identity is hidden.`;
        } else if (currentPrivacy === 'me' && currentViewerRelation === 'self') {            
            return `Only you and existing and requested collaborators are able to view this ${entityType}.`;
        } else if (currentViewerRelation) {
            switch (currentViewerRelation) {
                case 'admin':
                    return `You are able to view this ${entityType} because you are an admin.`;
                case 'self':
                    return `You are able to view this ${entityType} because you are the author.`;
                case 'coauthor_user':
                    return `You are able to view this ${entityType} because you are a collaborator on it.`;
                case 'coauthor_team':
                    return `You are able to view this ${entityType} because you are a member of a partea that is a collaborator on it.`;
                case 'coauthor_user_pending':
                    return `You are able to view this ${entityType} because the author requested you to collaborate on it.`;
                case 'coauthor_team_pending':
                    return `You are able to view this ${entityType} because the author requested a partea of which you are a member to collaborate on it.`;
                case 'bestea':
                    return `You are able to view this ${entityType} because you are a bestea of the author.`;
                case 'member':
                    return `You are able to view this ${entityType} because you are a member of a partea with the author.`;
                case 'moderator':
                    return `You are able to view this ${entityType} because you are a moderator of a partea with the author.`;
                case 'creator':
                    return `You are able to view this ${entityType} because you are the creator of a partea with the author.`;
                case 'follower':
                    return `You are able to view this ${entityType} because you are following the author.`;
                case 'team':
                    return `You are able to view this ${entityType} because you are in a partea with the author.`;
                default:
                    return `You are able to view this ${entityType} because you are ${currentViewerRelation}.`;
            }
        }
    }

    // Define privacy options based on actor type
    const privacyOptions: PrivacyOption[] = publicOnly
        ? [
            { value: "anonymous", label: "Anonymous", icon: HatGlasses },
            { value: "public", label: "Public", icon: Globe }
        ]
        : 
    actorType === "user" 
        ? [
            { value: "anonymous", label: "Anonymous", icon: HatGlasses },
            { value: "me", label: currentViewerRelation === 'self' ? editMode ? "Just me" : showDropdown ? "Just me" : "just you and existing and requested collaborators" : "just the author and existing and requested collaborators", icon: Lock },
            { value: "besteas", label: "Besteas", icon: HeartHandshake },
            { value: "follow", label: "Followers", icon: UserCheck },
            { value: "teams", label: "Parteas", icon: Users },
            { value: "public", label: "Public", icon: Globe },
        ].filter(option => !hideAnonymous || option.value !== "anonymous")
        : [
            { value: "anonymous", label: "Anonymous", icon: HatGlasses },
            { value: "members", label: "Partea members", icon: Lock },
            { value: "follow", label: "Followers", icon: UserCheck },
            { value: "teams", label: "Parteas", icon: Users },
            { value: "public", label: "Public", icon: Globe },
        ].filter(option => !hideAnonymous || option.value !== "anonymous");

    // Get the current privacy option details
    const getCurrentPrivacyOption = (): PrivacyOption => {
        const foundOption = privacyOptions.find(option => option.value === currentPrivacy);
        if (foundOption) {
            return foundOption;
        }

        // Prefer `public` whenever the current privacy value cannot
        // be honored — for example, when `publicOnly=true` slims
        // `privacyOptions` to just [anonymous, public] but the
        // caller's stored default audience is `me` / `besteas` /
        // `follow` / `teams`. Without this coalesce the old fallback
        // chain (`privacyOptions[4] || [3]` on user, `[3] || [2]` on
        // team) returned `undefined` and the next-line
        // `.icon` deref crashed the rebut composer.
        const publicOption = privacyOptions.find(option => option.value === 'public');
        if (publicOption) {
            return publicOption;
        }

        // Index fallbacks for the full (non-publicOnly) layout. We
        // try the typical "public" slot first per actor type, then
        // walk back, and finally surrender to the last available
        // option. The trailing `?? privacyOptions[0]` exists purely
        // to satisfy the return type when the array is empty — an
        // unreachable state at the moment but cheap insurance.
        const userFallback = privacyOptions[4] ?? privacyOptions[3] ?? privacyOptions[privacyOptions.length - 1];
        const teamFallback = privacyOptions[3] ?? privacyOptions[2] ?? privacyOptions[privacyOptions.length - 1];
        return (actorType === "user" ? userFallback : teamFallback) ?? privacyOptions[0];
    };

    const currentPrivacyOption = getCurrentPrivacyOption();
    const PrivacyIcon = currentPrivacyOption?.icon ?? Globe;

    /**
     * Handle privacy change - either update via API or just notify parent
     */
    const handlePrivacyChange = async (newPrivacy: PrivacyLevel) => {
        if (editMode && entityId && entityType) {
            // For existing entities, call the API
            try {
                setIsUpdating(true);
                await api.patch(apiRoutes.entities.privacy(entityType, entityId), {
                    privacy: newPrivacy
                });
                setCurrentPrivacy(newPrivacy);
                onPrivacyChange(newPrivacy);
                toast.success("Privacy updated successfully");
            } catch (error) {
                console.error("Error updating entity privacy:", error);
                toast.error("Failed to update privacy settings");
            } finally {
                setIsUpdating(false);
            }
        } else {
            // For new entities, just update the state and notify parent
            setCurrentPrivacy(newPrivacy);
            onPrivacyChange(newPrivacy);
        }
    };

    return (
        <div className={`space-y-2 ${!showDropdown && miniIcon && privacy === 'public' ? 'hidden' : ''} ${className}`}>
            <DropdownMenu>
                <div className="flex flex-row items-start">
                    {showDropdown ? (
                        <DropdownMenuTrigger 
                        className="flex items-center justify-between px-2 py-1.5 text-sm font-medium rounded-full bg-eoe-icon dark:bg-eoe-dark-icon hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover data-[state=open]:bg-eoe-bg-modal dark:data-[state=open]:bg-eoe-dark-bg-modal"
                        disabled={isUpdating || !showDropdown}
                    >
                        <div className="flex items-center gap-2 text-eoe-text dark:text-eoe-dark-text">
                            <PrivacyIcon className="size-3" />
                            {!miniIcon && <span>{currentPrivacyOption.label}</span>}
                        </div>
                    </DropdownMenuTrigger>
                    ) : (       
                        <Popover>
                            <PopoverTrigger 
                                className="flex items-center gap-1 text-sm font-medium text-eoe-text-sub dark:text-eoe-dark-text-sub"
                            onClick={(e) => e.stopPropagation()}
                            >                
                                <div 
                                    className="flex items-center justify-between px-1.5 py-1.5 text-sm font-medium rounded-full bg-eoe-icon dark:bg-eoe-dark-icon hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover data-[state=open]:bg-eoe-bg-modal dark:data-[state=open]:bg-eoe-dark-bg-modal"
                                    
                                >
                                    <div className="flex items-center gap-2 text-eoe-text dark:text-eoe-dark-text">
                                        <PrivacyIcon className="size-3" />
                                        {!miniIcon && <span>{currentPrivacyOption.label}</span>}
                                    </div>
                                </div>
                            </PopoverTrigger>
                        <PopoverContent className="max-w-[300px] space-y-1">
                            <p className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
                                Visible to {currentPrivacyOption.label === 'Anonymous' ? 'everyone' : currentPrivacyOption.label}.
                            </p>
                            <p className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
                                {getViewerMessage()}
                            </p>
                        </PopoverContent>
                    </Popover>
                    )}
                        <Popover>
                            <PopoverTrigger 
                                className="flex items-center gap-1 text-sm font-medium text-eoe-text-sub dark:text-eoe-dark-text-sub"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Eye className="size-3 cursor-pointer" />
                        </PopoverTrigger>
                        <PopoverContent className="max-w-[300px] space-y-1">
                            <p className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
                                Privacy options are ordered from most private to least private, and each option includes the users in preceding options.
                            </p>
                            <p className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
                                <strong>Anonymous</strong> posts are visible to everyone but hide your identity completely.
                            </p>
                            <p className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
                                You can change the default privacy level in <Link to="/settings/privacy" className="underline">settings</Link>.
                            </p>
                        </PopoverContent>
                    </Popover>
                </div>
            
                <DropdownMenuContent className="max-w-[300px]">
                    {privacyOptions.map((option) => (
                        <DropdownMenuItem 
                            key={option.value}
                            className={`${currentPrivacy === option.value ? "bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover" : ""}`}
                            onClick={() => handlePrivacyChange(option.value as PrivacyLevel)}
                        >
                            <option.icon className="size-4 mr-2" />
                            <span>{option.label}</span>
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
};

export default EntityPrivacy;
